/**
 * V113: MM 조직 설정 변경 훅
 *
 * setMmOrgEnabled callable을 호출하여 조직의 MM 사용 여부를 설정합니다.
 *
 * @module shared/hooks/useMmOrgSettingsMutation
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  SetMmOrgEnabledRequest,
  SetMmOrgEnabledResponse,
} from '../types/mm';

/**
 * MM 조직 설정 변경 훅 반환 타입
 */
export interface UseMmOrgSettingsMutationResult {
  /** 설정 변경 함수 */
  setMmOrgEnabled: (orgUnitId: string, enabled: boolean) => Promise<void>;

  /** 저장 중 상태 */
  isSaving: boolean;

  /** 에러 */
  error: Error | null;

  /** 에러 초기화 */
  clearError: () => void;
}

/**
 * MM 조직 설정 변경 훅
 *
 * @returns 설정 변경 함수 및 상태
 */
export function useMmOrgSettingsMutation(): UseMmOrgSettingsMutationResult {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const setMmOrgEnabled = useCallback(
    async (orgUnitId: string, enabled: boolean) => {
      setIsSaving(true);
      setError(null);

      try {
        // V113.1: firebase/config.ts에서 초기화된 functions 인스턴스 사용 (CORS 방지)
        if (!functions) {
          throw new Error('Firebase Functions가 초기화되지 않았습니다.');
        }

        const setMmOrgEnabledFn = httpsCallable<
          SetMmOrgEnabledRequest,
          SetMmOrgEnabledResponse
        >(functions, 'setMmOrgEnabled');

        const result = await setMmOrgEnabledFn({ orgUnitId, enabled });

        if (!result.data.success) {
          throw new Error(result.data.error || 'MM 설정 변경 실패');
        }

      } catch (err: any) {
        console.error('[V113] useMmOrgSettingsMutation error:', err);
        const error = err instanceof Error ? err : new Error(err.message || 'Unknown error');
        setError(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    setMmOrgEnabled,
    isSaving,
    error,
    clearError,
  };
}
